import React, { useEffect } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Collapse, Stack, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { OcppPasswordField } from '.'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { TextField, Toggle } from 'src/Libs/inputs/react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { OptionalFields } from 'src/Libs/flow/components'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
/**
 * Component for setting the behavior for 'CarAtHome'-Feature with CPO, OCPP Settings and authentication.
 * @param index Index of the evse-object in the evse-list
 */
export const CarAtHomeSection: React.FC<{ index: number }> = ({ index }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control, setValue, trigger } = useInstallationForm()

  const [isCarAtHomeBehaviorEnabled, isCredentialsActive, isNew] = useWatch({
    control,
    name: [
      `chargingStations.modbusTcp.${index}.carAtHomeBehavior.isEnabled`,
      `chargingStations.modbusTcp.${index}.carAtHomeBehavior.isCredentialsActive`,
      `chargingStations.modbusTcp.${index}.isNew`,
    ],
  })

  useEffect(() => {
    trigger(`chargingStations.modbusTcp.${index}`)
  }, [trigger, isCarAtHomeBehaviorEnabled, index])

  useEffect(() => {
    if (isCarAtHomeBehaviorEnabled) {
      setValue(`chargingStations.modbusTcp.${index}.accessManagementOptions`, 'badge', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }
  }, [index, isCarAtHomeBehaviorEnabled, setValue])

  return (
    <AccordionItem depth={1} title={t('installation.evseEditor.carAtHome.subTitle')} expanded elevation={0} sx={{ mb: 2 }}>
      <SettingsBox
        title={t('installation.evseEditor.carAtHome.subTitle')}
        description={t('installation.evseEditor.carAtHome.description')}
        suffix={
          <Stack sx={{ flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
            <Toggle disabled={isNew || !canEdit} name={`chargingStations.modbusTcp.${index}.carAtHomeBehavior.isEnabled`} />
            <Typography variant="body1" display="flex">
              {isCarAtHomeBehaviorEnabled ? t('common.enabled') : t('common.disabled')}
            </Typography>
          </Stack>
        }
        depth={1}
      />
      <OptionalFields<InstallationFormModel>
        condition={isCarAtHomeBehaviorEnabled}
        fieldNames={[
          `chargingStations.modbusTcp.${index}.carAtHomeBehavior.chargingPointOperatorWs`,
          `chargingStations.modbusTcp.${index}.carAtHomeBehavior.ocppId`,
          `chargingStations.modbusTcp.${index}.carAtHomeBehavior.isCredentialsActive`,
        ]}
      >
        <>
          <SettingsBox
            title={t('installation.evseEditor.carAtHome.CpoFieldTitle')}
            suffix={
              <TextField
                label={t('installation.evseEditor.carAtHome.CpoFieldTitle')}
                name={`chargingStations.modbusTcp.${index}.carAtHomeBehavior.chargingPointOperatorWs`}
                disabled={!canEdit}
              />
            }
            depth={1}
          />
          <SettingsBox
            title={t('installation.evseEditor.carAtHome.OcppIdFieldTitle')}
            suffix={
              <TextField
                label={t('installation.evseEditor.carAtHome.OcppIdFieldTitle')}
                name={`chargingStations.modbusTcp.${index}.carAtHomeBehavior.ocppId`}
                disabled={!canEdit}
              />
            }
            depth={1}
          />
          <SettingsBox
            title={t('installation.evseEditor.carAtHome.AuthTypeTitle')}
            suffix={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'flex-end' }}>
                <Stack sx={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Toggle name={`chargingStations.modbusTcp.${index}.carAtHomeBehavior.isCredentialsActive`} disabled={!canEdit} />
                  <Typography variant="body1" display="flex">
                    {isCredentialsActive ? t('installation.evseEditor.carAtHome.BasicAuth') : t('installation.evseEditor.carAtHome.NoAuth')}
                  </Typography>
                </Stack>
                <Collapse mountOnEnter unmountOnExit in={isCredentialsActive}>
                  <OcppPasswordField index={index} />
                </Collapse>
              </Box>
            }
            depth={1}
          />
        </>
      </OptionalFields>
    </AccordionItem>
  )
}
